import ReactGA from 'react-ga4';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useAuth from 'hooks/useAuth';

// Configuration for different environments
const GA4_CONFIG = {
    production: {
      measurementId: process.env.REACT_APP_GOOGLE_ANALYTICS_WEB_PROD_MID as string,
      debugMode: false
    },
    development: {
      measurementId: process.env.REACT_APP_GOOGLE_ANALYTICS_WEB_DEV_MID as string,
      debugMode: true
    }
  };

// Initialize GA4 with environment-specific config
const initGA = () => {
  try {
    const isDevelopment = process.env.NODE_ENV === 'development';
    const config = isDevelopment ? GA4_CONFIG.development : GA4_CONFIG.production;
    
    if (!config.measurementId) {
      console.warn('GA4 Measurement ID is not configured for the current environment');
      return;
    }
    
    // Initialize with both measurementId and gaOptions
    ReactGA.initialize([
      {
        trackingId: config.measurementId,
        gaOptions: {
          debug_mode: config.debugMode
        }
      }
    ]);
    console.log('GA4 initialized');
  } catch (error) {
    console.error('Failed to initialize GA4:', error);
  }
};

  // Track page views
const trackPageView = (
  path: string,
  userRole?: string,
  organizationId?: number | string,
) => {
    ReactGA.send({
      hitType: "pageview",
      page: path,
      title: userRole ? `${userRole} - ${path}` : path,
      user_role: userRole || 'Anonymous',
      organization_id: organizationId ? String(organizationId) : undefined
    });
  };

  // Track button clicks
const trackButtonClick = (
  buttonName: string,
  organizationId?: number | string,
) => {
    ReactGA.event('button_click', {
      category: 'User Interaction',
      label: buttonName,
      organization_id: organizationId ? String(organizationId) : undefined
    });
};

// Track form submissions
const trackFormSubmit = (
  formName: string,
  organizationId?: number | string,
) => {
    ReactGA.event('form_submit', {
      category: 'Form',
      label: formName,
      organization_id: organizationId ? String(organizationId) : undefined
    });
};

// Track login button click
const trackLoginClick = (organizationId?: number | string) => {
    ReactGA.event('login_button_click', {
      category: 'Authentication',
      organization_id: organizationId ? String(organizationId) : undefined
    });
};

// Track register button click
const trackRegisterClick = (organizationId?: number | string) => {
    ReactGA.event('register_button_click', {
      category: 'Authentication',
      organization_id: organizationId ? String(organizationId) : undefined
    });
};

// Custom hook that combines analytics functionality
export const useAnalytics = () => {
    const location = useLocation();
    const { user } = useAuth();
    const organizationId = (user as any)?.region?.organization?.id;
    
    // Initialize GA4 only once when the hook is first mounted
    useEffect(() => {
      // Initialize analytics when hook is mounted
      initGA();
    }, []); // Empty dependency array means this only runs once
    
    // Track page views when location changes
    useEffect(() => {
      // Track page view with user role
      trackPageView(location.pathname, user?.role, organizationId);
    }, [location.pathname, user?.role, organizationId]);

    useEffect(() => {
      if (user?.role) {
        ReactGA.set({
          user_role: user.role,
          organization_id: organizationId ? String(organizationId) : undefined
        });
      }
    }, [user?.role, organizationId]);
    
    return {
      trackPageView
    };
};

  // Export individual functions for direct use if needed
export const WebAnalytics = {
    initGA,
    trackPageView,
    trackButtonClick,
    trackFormSubmit,
    trackLoginClick,
    trackRegisterClick
  };